import apiClient from './client';
import type { Conversation, Message } from '../types/message';
import type { User } from '../types/user';
import type { FriendRequest, GroupMember } from '../types/people';

export const api = {
  // ---- 认证 ----
  authConfig: () => apiClient.get<{ oidc_configured: boolean; dev_enabled: boolean; issuer: string }>('/auth/config').then((r) => r.data),
  me: () => apiClient.get<User>('/auth/me').then((r) => r.data),
  logout: () => apiClient.post('/auth/logout'),
  updateProfile: (payload: { nickname?: string; avatar?: string }) =>
    apiClient.patch<{ user: User }>('/users/me', payload).then((r) => r.data.user),

  // ---- 联系人 / 好友申请 ----
  contacts: () => apiClient.get<{ contacts: User[] }>('/contacts').then((r) => r.data.contacts),
  addContact: (payload: { user_id: number; greeting?: string }) =>
    apiClient
      .post<{ request_id?: number; user: User; auto_accepted?: boolean }>('/contacts', payload)
      .then((r) => r.data),
  deleteContact: (id: number) => apiClient.delete(`/contacts/${id}`),
  friendRequests: () => apiClient.get<{ requests: FriendRequest[] }>('/contacts/requests').then((r) => r.data.requests),
  friendRequestsAll: () => apiClient.get<{ requests: FriendRequest[] }>('/contacts/requests/all').then((r) => r.data.requests),
  greetFriendRequest: (id: number, content: string) => apiClient.post(`/contacts/requests/${id}/greet`, { content }),
  acceptFriendRequest: (id: number) => apiClient.post(`/contacts/requests/${id}/accept`),
  rejectFriendRequest: (id: number) => apiClient.post(`/contacts/requests/${id}/reject`),
  searchUsers: (q: string) => apiClient.get<{ users: User[] }>(`/users/search?q=${encodeURIComponent(q)}`).then((r) => r.data.users),

  // ---- 拉黑 ----
  blockedUsers: () => apiClient.get<{ blocked: User[] }>('/blocks').then((r) => r.data.blocked),
  blockUser: (id: number) => apiClient.post('/blocks', { user_id: id }),
  unblockUser: (id: number) => apiClient.delete(`/blocks/${id}`),

  // ---- 会话 ----
  conversations: () => apiClient.get<{ conversations: Conversation[] }>('/conversations').then((r) => r.data.conversations),
  createGroup: (name: string, memberIds: number[]) =>
    apiClient.post<{ conversation: Conversation }>('/conversations', { name, member_ids: memberIds }).then((r) => r.data.conversation),
  openPrivate: (userId: number) =>
    apiClient.post<{ conversation: Conversation }>('/conversations/private', { user_id: userId }).then((r) => r.data.conversation),
  messages: (cid: number, before = 0, limit = 50) =>
    apiClient.get<{ messages: Message[] }>(`/conversations/${cid}/messages?before=${before}&limit=${limit}`).then((r) => r.data.messages),
  searchMessages: (cid: number, q: string) =>
    apiClient.get<{ messages: Message[] }>(`/conversations/${cid}/messages/search?q=${encodeURIComponent(q)}`).then((r) => r.data.messages),
  markRead: (cid: number) => apiClient.post(`/conversations/${cid}/read`),
  muteSelf: (cid: number, muted: boolean) => apiClient.post(`/conversations/${cid}/mute-self`, { muted }),
  conversationMembers: (cid: number) =>
    apiClient.get<{ members: GroupMember[] }>(`/conversations/${cid}/members`).then((r) => r.data.members),
  addMember: (cid: number, userId: number) => apiClient.post(`/conversations/${cid}/members`, { user_id: userId }),
  removeMember: (cid: number, userId: number) => apiClient.delete(`/conversations/${cid}/members/${userId}`),
  updateGroup: (cid: number, name: string) => apiClient.patch(`/conversations/${cid}`, { name }),
  muteGroup: (cid: number, muted: boolean) => apiClient.post(`/conversations/${cid}/mute`, { muted }),
  transferGroup: (cid: number, userId: number) => apiClient.post(`/conversations/${cid}/transfer`, { user_id: userId }),
  dissolveGroup: (cid: number) => apiClient.post(`/conversations/${cid}/dissolve`),

  // ---- 消息操作 ----
  deleteMessage: (mid: number) => apiClient.delete(`/messages/${mid}`),
  recallMessage: (mid: number) => apiClient.post(`/messages/${mid}/recall`),
};
